import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Form,
  Input,
  Upload,
  Button,
  Space,
  Typography,
  message,
  Select,
  InputNumber,
  Switch,
  Tabs,
} from "antd";
import { UploadOutlined, SaveOutlined } from "@ant-design/icons";
import {
  readCachedSystemConfig,
  fetchSystemConfig,
  saveSystemConfig,
} from "../../../shared/lib/systemConfig";
import "../../../shared/styles/ErpPages.css";

const { Text } = Typography;

const applyFormValues = (form, config) => {
  if (!config) return;
  form.setFieldsValue({
    site_name: config.site_name,
    login_logo_url: config.login_logo_url || config.login_logo || "",
    sidebar_logo_url: config.sidebar_logo_url || config.sidebar_logo || "",
    currency_code: config.currency_code || "IQD",
    secondary_currency_code: config.secondary_currency_code || "",
    currency_display: config.currency_display || "code",
    locale: config.locale || "en-US",
    fraction_digits: config.fraction_digits ?? 0,
    invoice_company_name: config.invoice_company_name ?? "",
    invoice_tagline: config.invoice_tagline ?? "",
    invoice_logo_url: config.invoice_logo_url || config.invoice_logo || "",
    invoice_stamp_image_url: config.invoice_stamp_image_url || config.invoice_stamp_image || "",
    invoice_address: config.invoice_address ?? "",
    invoice_phones: config.invoice_phones ?? "",
    invoice_email: config.invoice_email ?? "",
    invoice_website: config.invoice_website ?? "",
    invoice_footer_note: config.invoice_footer_note ?? "",
    invoice_watermark_enabled: config.invoice_watermark_enabled !== false,
    invoice_watermark_opacity: config.invoice_watermark_opacity ?? 0.07,
  });
};

const SystemSettings = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [loginFileList, setLoginFileList] = useState([]);
  const [sidebarFileList, setSidebarFileList] = useState([]);
  const [invoiceLogoFileList, setInvoiceLogoFileList] = useState([]);
  const [invoiceStampFileList, setInvoiceStampFileList] = useState([]);
  const [currentConfig, setCurrentConfig] = useState(readCachedSystemConfig());

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const config = await fetchSystemConfig();
        setCurrentConfig(config);
        applyFormValues(form, config);
      } catch (error) {
        const cached = readCachedSystemConfig();
        if (cached) {
          setCurrentConfig(cached);
          applyFormValues(form, cached);
          message.warning(t("settingsWarnCached"));
        } else {
          message.error(t("settingsErrLoad"));
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [form, t]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        site_name: values.site_name,
        login_logo_url: values.login_logo_url || "",
        sidebar_logo_url: values.sidebar_logo_url || "",
        currency_code: values.currency_code || "IQD",
        secondary_currency_code: (values.secondary_currency_code || "").trim() || null,
        currency_display: values.currency_display || "code",
        locale: values.locale || "en-US",
        fraction_digits: typeof values.fraction_digits === "number" ? values.fraction_digits : 0,
        invoice_company_name: values.invoice_company_name || "",
        invoice_tagline: values.invoice_tagline || "",
        invoice_logo_url: values.invoice_logo_url || "",
        invoice_stamp_image_url: values.invoice_stamp_image_url || "",
        invoice_address: values.invoice_address || "",
        invoice_phones: values.invoice_phones || "",
        invoice_email: values.invoice_email || "",
        invoice_website: values.invoice_website || "",
        invoice_footer_note: values.invoice_footer_note || "",
        invoice_watermark_enabled: Boolean(values.invoice_watermark_enabled),
        invoice_watermark_opacity:
          typeof values.invoice_watermark_opacity === "number"
            ? values.invoice_watermark_opacity
            : parseFloat(values.invoice_watermark_opacity) || 0.07,
      };

      if (loginFileList[0]?.originFileObj) {
        payload.login_logo = loginFileList[0].originFileObj;
      }
      if (sidebarFileList[0]?.originFileObj) {
        payload.sidebar_logo = sidebarFileList[0].originFileObj;
      }
      if (invoiceLogoFileList[0]?.originFileObj) {
        payload.invoice_logo = invoiceLogoFileList[0].originFileObj;
      }
      if (invoiceStampFileList[0]?.originFileObj) {
        payload.invoice_stamp_image = invoiceStampFileList[0].originFileObj;
      }

      setSaving(true);
      const saved = await saveSystemConfig(payload);
      setCurrentConfig(saved);
      setLoginFileList([]);
      setSidebarFileList([]);
      setInvoiceLogoFileList([]);
      setInvoiceStampFileList([]);
      applyFormValues(form, saved);
      message.success(t("settingsSaveSuccess"));
    } catch (error) {
      if (error?.errorFields) {
        const first = error.errorFields[0]?.name?.[0];
        if (["site_name"].includes(first)) setActiveTab("general");
        else if (
          ["currency_code", "secondary_currency_code", "currency_display", "locale", "fraction_digits"].includes(
            first
          )
        ) {
          setActiveTab("currency");
        } else if (
          String(first || "").startsWith("invoice_") ||
          ["login_logo_url", "sidebar_logo_url"].includes(first)
        ) {
          if (String(first || "").startsWith("invoice_")) setActiveTab("invoice");
          else setActiveTab("branding");
        }
        return;
      }
      const errs = error?.response?.data?.errors;
      const firstField =
        errs && typeof errs === "object" ? Object.values(errs).flat()?.[0] : null;
      const serverMessage = error?.response?.data?.message;
      message.error(firstField || serverMessage || t("settingsSaveError"));
    } finally {
      setSaving(false);
    }
  };

  const uploadBlock = (label, fileList, setFileList, pickLabel) => (
    <Space direction="vertical" size={6} style={{ marginBottom: 16, width: "100%" }}>
      <Text strong style={{ fontSize: 12 }}>
        {label}
      </Text>
      <Upload
        fileList={fileList}
        beforeUpload={() => false}
        onChange={({ fileList: next }) => setFileList(next.slice(-1))}
        maxCount={1}
        accept="image/*"
      >
        <Button icon={<UploadOutlined />}>{pickLabel}</Button>
      </Upload>
    </Space>
  );

  return (
    <div className="erp-page">
      <div className="erp-shell">
        <div className="erp-header">
          <div>
            <div className="erp-breadcrumb">
              {t("sidebarAdministration", "Administration")} / {t("systemSettings", "System settings")}
            </div>
            <h1>{t("systemSettings", "System settings")}</h1>
          </div>
          <Space>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
              {t("settingsSave", "Save")}
            </Button>
          </Space>
        </div>

        {currentConfig?.site_name ? (
          <div className="erp-meta">
            <span>
              {t("settingsCurrentSiteName", { name: currentConfig.site_name })}
            </span>
          </div>
        ) : null}

        <div className="erp-body" style={{ padding: 14 }}>
          <Form form={form} layout="vertical" disabled={loading}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              className="erp-settings-tabs"
              items={[
                {
                  key: "general",
                  label: t("settingsTabGeneral", "General"),
                  children: (
                    <div className="erp-settings-pane">
                      <Form.Item
                        name="site_name"
                        label={t("settingsWebsiteName")}
                        rules={[{ required: true, message: t("settingsWebsiteNameRequired") }]}
                      >
                        <Input placeholder={t("settingsWebsiteNamePlaceholder")} />
                      </Form.Item>
                    </div>
                  ),
                },
                {
                  key: "currency",
                  label: t("settingsTabCurrency", "Currency"),
                  children: (
                    <div className="erp-settings-pane">
                      <Form.Item
                        name="currency_code"
                        label={t("settingsMainCurrencyCode")}
                        rules={[{ required: true, message: t("settingsCurrencyCodeRequired") }]}
                      >
                        <Input placeholder="IQD" maxLength={10} />
                      </Form.Item>
                      <Form.Item
                        name="secondary_currency_code"
                        label={t("settingsSecondaryCurrencyOptional")}
                      >
                        <Input placeholder={t("settingsSecondaryCurrencyPlaceholder")} maxLength={10} />
                      </Form.Item>
                      <Form.Item
                        name="currency_display"
                        label={t("settingsCurrencyDisplay")}
                        rules={[{ required: true }]}
                      >
                        <Select
                          options={[
                            { value: "code", label: t("settingsCurrencyDisplayCode") },
                            { value: "symbol", label: t("settingsCurrencyDisplaySymbol") },
                            { value: "name", label: t("settingsCurrencyDisplayName") },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item
                        name="locale"
                        label={t("settingsLocale")}
                        rules={[{ required: true, message: t("settingsLocaleRequired") }]}
                      >
                        <Input placeholder={t("settingsLocalePlaceholder")} maxLength={35} />
                      </Form.Item>
                      <Form.Item
                        name="fraction_digits"
                        label={t("settingsFractionDigits")}
                        rules={[{ required: true }]}
                      >
                        <InputNumber min={0} max={6} style={{ width: "100%" }} />
                      </Form.Item>
                    </div>
                  ),
                },
                {
                  key: "branding",
                  label: t("settingsTabBranding", "Branding"),
                  children: (
                    <div className="erp-settings-pane">
                      <Form.Item name="login_logo_url" label={t("settingsLoginLogoUrl")}>
                        <Input placeholder={t("settingsLoginLogoUrlPlaceholder")} />
                      </Form.Item>
                      {uploadBlock(
                        t("settingsUploadLoginLogo"),
                        loginFileList,
                        setLoginFileList,
                        t("settingsSelectLoginLogo")
                      )}
                      <Form.Item name="sidebar_logo_url" label={t("settingsSidebarLogoUrl")}>
                        <Input placeholder={t("settingsSidebarLogoUrlPlaceholder")} />
                      </Form.Item>
                      {uploadBlock(
                        t("settingsUploadSidebarLogo"),
                        sidebarFileList,
                        setSidebarFileList,
                        t("settingsSelectSidebarLogo")
                      )}
                    </div>
                  ),
                },
                {
                  key: "invoice",
                  label: t("settingsTabInvoice", "Invoice"),
                  children: (
                    <div className="erp-settings-pane">
                      <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 12 }}>
                        {t("settingsInvoiceSectionHint")}
                      </Text>
                      <Form.Item name="invoice_company_name" label={t("settingsInvoiceCompanyTitle")}>
                        <Input placeholder={t("settingsInvoiceCompanyTitlePlaceholder")} />
                      </Form.Item>
                      <Form.Item name="invoice_tagline" label={t("settingsInvoiceTagline")}>
                        <Input placeholder={t("settingsInvoiceTaglinePlaceholder")} />
                      </Form.Item>
                      <Form.Item name="invoice_logo_url" label={t("settingsInvoiceLogoUrl")}>
                        <Input placeholder={t("settingsInvoiceLogoUrlPlaceholder")} />
                      </Form.Item>
                      {uploadBlock(
                        t("settingsInvoiceLogoUpload"),
                        invoiceLogoFileList,
                        setInvoiceLogoFileList,
                        t("settingsInvoiceLogoPick")
                      )}
                      <Form.Item name="invoice_address" label={t("settingsInvoiceAddress")}>
                        <Input.TextArea rows={3} placeholder={t("settingsInvoiceAddressPlaceholder")} />
                      </Form.Item>
                      <Form.Item name="invoice_phones" label={t("settingsInvoicePhones")}>
                        <Input.TextArea rows={2} placeholder={t("settingsInvoicePhonesPlaceholder")} />
                      </Form.Item>
                      <Form.Item name="invoice_email" label={t("settingsInvoiceEmail")}>
                        <Input type="email" placeholder={t("settingsInvoiceEmailPlaceholder")} />
                      </Form.Item>
                      <Form.Item name="invoice_website" label={t("settingsInvoiceWebsite")}>
                        <Input placeholder={t("settingsInvoiceWebsitePlaceholder")} />
                      </Form.Item>
                      <Form.Item name="invoice_footer_note" label={t("settingsInvoiceFooterNote")}>
                        <Input.TextArea rows={2} placeholder={t("settingsInvoiceFooterNotePlaceholder")} />
                      </Form.Item>
                      <Form.Item name="invoice_stamp_image_url" label={t("settingsInvoiceStampUrl")}>
                        <Input placeholder={t("settingsInvoiceStampUrlPlaceholder")} />
                      </Form.Item>
                      {uploadBlock(
                        t("settingsInvoiceStampUpload"),
                        invoiceStampFileList,
                        setInvoiceStampFileList,
                        t("settingsInvoiceStampPick")
                      )}
                      <Form.Item
                        name="invoice_watermark_enabled"
                        label={t("settingsInvoiceWatermarkEnabled")}
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                      <Form.Item
                        name="invoice_watermark_opacity"
                        label={t("settingsInvoiceWatermarkOpacity")}
                        tooltip={t("settingsInvoiceWatermarkOpacityHint")}
                      >
                        <InputNumber min={0.02} max={0.5} step={0.01} style={{ width: "100%" }} />
                      </Form.Item>
                    </div>
                  ),
                },
              ]}
            />
          </Form>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
